import React, { useEffect, useRef, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import authService from "../services/authService";
import menuVisibilityService from "../services/menuVisibilityService";
import kkpService from "../services/kkpService";
import depotService from "../services/depotService";
import { ROUTES } from "../config/routes";
import AssistantChatWidget from "../components/assistant/AssistantChatWidget";
import {
  getPubGraphisteAFaireCount,
  setPubGraphisteAFaireCount,
  subscribePubGraphisteAFaireCount,
} from "../utils/pubGraphisteCountStore";
import {
  getDepotPendingCount,
  setDepotPendingCount,
  subscribeDepotPendingCount,
} from "../utils/depotPendingCountStore";
import "../styles/sidebar-menu-notif.scss";
import "../styles/app-ui.scss";

const userInitials = (user) => {
  const first = String(user?.firstName || "").trim();
  const last = String(user?.lastName || "").trim();
  if (first && last) return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
  if (first) return first.slice(0, 2).toUpperCase();
  return "?";
};

const userShortName = (user) => {
  const first = String(user?.firstName || "").trim();
  const last = String(user?.lastName || "").trim();
  if (!first) return "Compte";
  if (!last) return first;
  return `${first} ${last.charAt(0).toUpperCase()}.`;
};

const rubriqueKeyFromPath = (pathname) => {
  if (pathname.startsWith('/secteurs')) return 'secteurs';
  if (pathname.startsWith('/agenda')) return 'agenda';
  if (pathname.startsWith('/iti')) return 'iti';
  if (pathname.startsWith('/kkp')) return 'kkp';
  if (pathname.startsWith('/temps')) return 'temps';
  if (pathname.startsWith('/admin')) return 'admin';
  if (pathname.startsWith('/klub/magazine')) return 'kkp';
  if (pathname.startsWith('/klub')) return 'klub';
  return null;
};

const RubriqueTitle = ({ label, toneClass, collapsible, open, onToggle }) => (
  <li
    className={[
      'sidebar-menu-group-title',
      'sidebar-menu-group-title--rubrique',
      toneClass,
      collapsible ? 'sidebar-menu-group-title--toggle' : '',
      collapsible && open ? 'is-open' : '',
      collapsible && !open ? 'is-collapsed' : '',
    ].filter(Boolean).join(' ')}
  >
    {collapsible ? (
      <button
        type="button"
        className="sidebar-menu-group-title__btn"
        aria-expanded={open}
        aria-label={open ? `Replier ${label}` : `Déplier ${label}`}
        onClick={onToggle}
      >
        <span>{label}</span>
        <Icon
          icon={open ? 'mdi:chevron-up' : 'mdi:chevron-down'}
          className="sidebar-menu-group-title__chevron"
          aria-hidden
        />
      </button>
    ) : (
      label
    )}
  </li>
);

const MasterLayout = ({ children }) => {
  let [sidebarActive, seSidebarActive] = useState(false);
  let [mobileMenu, setMobileMenu] = useState(false);
  const [sidebarHovered, setSidebarHovered] = useState(false);
  const [openRubriques, setOpenRubriques] = useState(() => {
    const key = rubriqueKeyFromPath(window.location.pathname);
    return key ? new Set([key]) : new Set();
  });
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState(() => authService.getCurrentUser());
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);
  const [menuTick, setMenuTick] = useState(0);
  const [pubGraphisteAFaireCount, setPubGraphisteAFaireCountState] = useState(
    () => getPubGraphisteAFaireCount() ?? 0
  );
  const [depotPendingCount, setDepotPendingCountState] = useState(
    () => getDepotPendingCount() ?? 0
  );

  const canSee = (key) => menuVisibilityService.canSee(key);

  useEffect(() => {
    let cancelled = false;
    menuVisibilityService.load()
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setMenuTick((t) => t + 1);
      });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const unsubscribe = subscribePubGraphisteAFaireCount((total) => {
      setPubGraphisteAFaireCountState(total);
    });

    let cancelled = false;
    const loadCount = async () => {
      if (!menuVisibilityService.isLoaded() && !authService.isSuperAdmin()) {
        return;
      }
      if (!canSee('page.kkp.cdfPubGraphiste')) {
        if (!cancelled) setPubGraphisteAFaireCount(0);
        return;
      }
      try {
        const data = await kkpService.getPubGraphisteAFaireCount();
        if (!cancelled) {
          const total = Number(data?.total);
          setPubGraphisteAFaireCount(Number.isFinite(total) ? total : 0);
        }
      } catch (err) {
        console.warn('Compteur créa pub indisponible:', err?.message || err);
      }
    };

    loadCount();

    return () => {
      cancelled = true;
      unsubscribe();
    };
    // menuTick : recharger une fois les permissions menu disponibles
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuTick, location.pathname]);

  useEffect(() => {
    const unsubscribe = subscribeDepotPendingCount((total) => {
      setDepotPendingCountState(total);
    });

    let cancelled = false;
    const loadCount = async () => {
      if (!menuVisibilityService.isLoaded() && !authService.isSuperAdmin()) {
        return;
      }
      if (!canSee('page.depotRdv')) {
        if (!cancelled) setDepotPendingCount(0);
        return;
      }
      try {
        const data = await depotService.getPendingCount();
        if (!cancelled) {
          const total = Number(data?.total);
          setDepotPendingCount(Number.isFinite(total) ? total : 0);
        }
      } catch (err) {
        console.warn('Compteur dépôts à relire indisponible:', err?.message || err);
      }
    };

    loadCount();

    return () => {
      cancelled = true;
      unsubscribe();
    };
    // menuTick : recharger une fois les permissions menu disponibles
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuTick, location.pathname]);

  const isItiCommunicationRoute = location.pathname.startsWith('/iti/domaines')
    || location.pathname.startsWith('/iti/hebergement');
  const isItiMaintenanceRoute = location.pathname.startsWith('/iti/maintenance');
  const isItiAdminRoute = location.pathname.startsWith('/iti/admin');
  const isSecteursRoute = location.pathname.startsWith("/secteurs/");
  const isKkpRoute = location.pathname.startsWith("/kkp/");
  const isKkpDistribRoute = location.pathname.startsWith("/kkp/distribution");
  const isKkpVentesRoute = isKkpRoute
    && !isKkpDistribRoute
    && !location.pathname.startsWith("/kkp/plan-de-com")
    && !location.pathname.startsWith("/kkp/chiffrage")
    && !location.pathname.startsWith("/kkp/admin");
  const isKkpAdminRoute = (location.pathname ?? "").startsWith("/kkp/admin");
  const isKlubChiffrageRoute = location.pathname.startsWith("/klub/chiffrage");
  const isKlubMagazineRoute = location.pathname.startsWith("/klub/magazine");
  const isKlubRoute = location.pathname.startsWith("/klub/")
    && !isKlubChiffrageRoute
    && !isKlubMagazineRoute;
  const isKlubAdminRoute = (location.pathname ?? "").startsWith("/klub/admin");
  const isTempsRoute = location.pathname.startsWith("/temps/");
  const isAssistantRoute = location.pathname.startsWith("/assistant");

  const roleList = Array.isArray(user?.roles) ? user.roles : [];
  const hasMenuRole = (role) => roleList.includes(role) || roleList.includes(role.replace(/^ROLE_/, ''));
  const collapseRubriques = hasMenuRole('ROLE_SUPERADMIN')
    || hasMenuRole('ROLE_COMMERCIAL')
    || hasMenuRole('ROLE_ADMIN_COMMERCIAL');
  const railIconsOnly = collapseRubriques && sidebarActive && !sidebarHovered && !mobileMenu;
  const visibleRubriquesKey = !collapseRubriques
    ? 'all'
    : railIconsOnly
      ? 'rail'
      : [...openRubriques].sort().join('|');

  const showRubriqueItems = (key) => {
    if (!collapseRubriques) return true;
    if (railIconsOnly) return true;
    return openRubriques.has(key);
  };

  const toggleRubrique = (key) => {
    setOpenRubriques((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const refreshOpenSubmenuHeights = () => {
    document.querySelectorAll(".sidebar-menu .dropdown.open").forEach((dropdown) => {
      const submenu = dropdown.querySelector(".sidebar-submenu");
      if (!submenu) return;
      // Mesure sans contrainte : un maxHeight trop bas peut tronquer le dernier item.
      submenu.style.maxHeight = "none";
      const height = submenu.scrollHeight;
      // Petite marge : icônes Iconify / polices peuvent encore peaufiner le layout.
      submenu.style.maxHeight = `${Math.ceil(height) + 16}px`;
    });
  };

  const queueSubmenuHeightRefresh = (timers) => {
    requestAnimationFrame(() => {
      refreshOpenSubmenuHeights();
      requestAnimationFrame(refreshOpenSubmenuHeights);
    });
    timers.push(
      window.setTimeout(refreshOpenSubmenuHeights, 0),
      window.setTimeout(refreshOpenSubmenuHeights, 50),
      window.setTimeout(refreshOpenSubmenuHeights, 320),
    );
  };

  useEffect(() => {
    setUser(authService.getCurrentUser());
    setMobileMenu(false);
    setUserMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!collapseRubriques) return;
    const key = rubriqueKeyFromPath(location.pathname);
    setOpenRubriques(key ? new Set([key]) : new Set());
  }, [location.pathname, collapseRubriques]);

  useEffect(() => {
    if (!userMenuOpen) return undefined;

    const onPointerDown = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setUserMenuOpen(false);
      }
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        setUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [userMenuOpen]);

  const handleLogout = () => {
    setUserMenuOpen(false);
    authService.logout();
    navigate(ROUTES.login);
  };

  useEffect(() => {
    const heightTimers = [];

    const handleDropdownClick = (event) => {
      event.preventDefault();
      const clickedLink = event.currentTarget;
      const clickedDropdown = clickedLink.closest(".dropdown");

      if (!clickedDropdown) return;

      const isActive = clickedDropdown.classList.contains("open");

      const allDropdowns = document.querySelectorAll(".sidebar-menu .dropdown");
      allDropdowns.forEach((dropdown) => {
        dropdown.classList.remove("open");
        const submenu = dropdown.querySelector(".sidebar-submenu");
        if (submenu) {
          submenu.style.maxHeight = "0px";
        }
      });

      if (!isActive) {
        clickedDropdown.classList.add("open");
        queueSubmenuHeightRefresh(heightTimers);
      }
    };

    const dropdownTriggers = document.querySelectorAll(
      ".sidebar-menu .dropdown > a, .sidebar-menu .dropdown > Link"
    );

    dropdownTriggers.forEach((trigger) => {
      trigger.addEventListener("click", handleDropdownClick);
    });

    const openActiveDropdown = () => {
      const allDropdowns = document.querySelectorAll(".sidebar-menu .dropdown");
      allDropdowns.forEach((dropdown) => {
        dropdown.classList.remove("open");
        const submenu = dropdown.querySelector(".sidebar-submenu");
        if (submenu) {
          submenu.style.maxHeight = "0px";
        }
      });

      allDropdowns.forEach((dropdown) => {
        const submenuLinks = dropdown.querySelectorAll(".sidebar-submenu li a");
        submenuLinks.forEach((link) => {
          if (
            link.getAttribute("href") === location.pathname ||
            link.getAttribute("to") === location.pathname
          ) {
            dropdown.classList.add("open");
          }
        });
      });

      if (isItiCommunicationRoute) {
        const dropdown = document.querySelector(".sidebar-menu .dropdown[data-menu='iti-communication']");
        if (dropdown) {
          dropdown.classList.add("open");
        }
      }

      if (isItiMaintenanceRoute) {
        const dropdown = document.querySelector(".sidebar-menu .dropdown[data-menu='iti-maintenance']");
        if (dropdown) {
          dropdown.classList.add("open");
        }
      }

      if (isSecteursRoute) {
        const secteursDropdown = document.querySelector(".sidebar-menu .dropdown[data-menu='secteurs']");
        if (secteursDropdown) {
          secteursDropdown.classList.add("open");
        }
      }

      if (isKkpVentesRoute) {
        const kkpDropdown = document.querySelector(".sidebar-menu .dropdown[data-menu='kkp-ventes']");
        if (kkpDropdown) {
          kkpDropdown.classList.add("open");
        }
      }

      if (isKkpDistribRoute) {
        const distribDropdown = document.querySelector(".sidebar-menu .dropdown[data-menu='kkp-distrib']");
        if (distribDropdown) {
          distribDropdown.classList.add("open");
        }
      }

      if (isKlubRoute) {
        const klubDropdown = document.querySelector(".sidebar-menu .dropdown[data-menu='klub']");
        if (klubDropdown) {
          klubDropdown.classList.add("open");
        }
      }

      if (isTempsRoute) {
        const tempsDropdown = document.querySelector(".sidebar-menu .dropdown[data-menu='temps']");
        if (tempsDropdown) {
          tempsDropdown.classList.add("open");
        }
      }

      queueSubmenuHeightRefresh(heightTimers);
    };

    openActiveDropdown();

    return () => {
      heightTimers.forEach((id) => window.clearTimeout(id));
      dropdownTriggers.forEach((trigger) => {
        trigger.removeEventListener("click", handleDropdownClick);
      });
    };
  }, [location.pathname, isItiCommunicationRoute, isItiMaintenanceRoute, isSecteursRoute, isKkpVentesRoute, isKkpDistribRoute, isKlubRoute, isTempsRoute, menuTick, visibleRubriquesKey]);

  let sidebarControl = () => {
    seSidebarActive(!sidebarActive);
  };

  let mobileMenuControl = () => {
    setMobileMenu(!mobileMenu);
  };

  const disabledModules = [];

  return (
    <section className={mobileMenu ? "overlay active" : "overlay "}>
      <aside
        className={
          sidebarActive
            ? "sidebar active "
            : mobileMenu
            ? "sidebar sidebar-open"
            : "sidebar"
        }
        onMouseEnter={() => setSidebarHovered(true)}
        onMouseLeave={() => setSidebarHovered(false)}
      >
        <button
          onClick={mobileMenuControl}
          type='button'
          className='sidebar-close-btn'
        >
          <Icon icon='radix-icons:cross-2' />
        </button>
        <div>
          <Link to={ROUTES.home} className='sidebar-logo'>
            <img
              src='/assets/images/logo_konvergence.png'
              alt='Konvergence'
              className='light-logo'
              style={{ maxHeight: '52px', width: 'auto' }}
            />
            <img
              src='/assets/images/logo_konvergence.png'
              alt='Konvergence'
              className='dark-logo'
              style={{ maxHeight: '52px', width: 'auto' }}
            />
            <img
              src='/assets/images/favicon_konvergence.png'
              alt='Konvergence'
              className='logo-icon'
              style={{ maxHeight: '36px', width: 'auto' }}
            />
          </Link>
        </div>
        <div className='sidebar-menu-area'>
          <ul className='sidebar-menu' id='sidebar-menu'>
            {canSee('page.home') && (
            <li>
              <NavLink
                to={ROUTES.home}
                end
                className={(navData) =>
                  navData.isActive ? "active-page" : ""
                }
              >
                <Icon icon='solar:home-smile-angle-outline' className='menu-icon' />
                <span>Accueil</span>
              </NavLink>
            </li>
            )}

            {canSee('page.assistant') && (
            <li>
              <NavLink
                to={ROUTES.assistant}
                className={(navData) =>
                  navData.isActive ? "active-page" : ""
                }
              >
                <Icon icon='mdi:robot-outline' className='menu-icon' />
                <span>Assistant itiConseil</span>
              </NavLink>
            </li>
            )}

            {canSee('page.depotRdv') && (
            <li>
              <NavLink
                to={ROUTES.depots}
                className={(navData) =>
                  navData.isActive ? "active-page" : ""
                }
              >
                <Icon icon='mdi:folder-upload-outline' className='menu-icon' />
                <span className="sidebar-menu-label">Dépôts</span>
                {depotPendingCount > 0 && (
                  <b
                    className="sidebar-menu-notif-badge"
                    title={`${depotPendingCount} dépôt${depotPendingCount > 1 ? 's' : ''} à relire`}
                    aria-label={`${depotPendingCount} dépôt${depotPendingCount > 1 ? 's' : ''} à relire`}
                  >
                    {depotPendingCount > 99 ? '99+' : depotPendingCount}
                  </b>
                )}
              </NavLink>
            </li>
            )}

            {canSee('section.secteurs') && (
            <>
            <RubriqueTitle
              label="Secteurs"
              toneClass="sidebar-menu-group-title--secteurs"
              collapsible={collapseRubriques}
              open={openRubriques.has('secteurs')}
              onToggle={() => toggleRubrique('secteurs')}
            />
            {showRubriqueItems('secteurs') && (<>
            {canSee('menu.secteurs') && (
            <li className='dropdown' data-menu='secteurs'>
              <Link to='#'>
                <Icon icon='mdi:map-marker-radius' className='menu-icon' />
                <span>Prospection</span>
              </Link>
              <ul className='sidebar-submenu'>
                {canSee('page.secteurs.carte') && (
                <li>
                  <NavLink
                    to={ROUTES.secteurs.carte}
                    className={(navData) =>
                      navData.isActive ? "active-page" : ""
                    }
                  >
                    <Icon icon='mdi:map-outline' className='menu-icon' />
                    Carte des zones
                  </NavLink>
                </li>
                )}
                {canSee('page.secteurs.entreprises') && (
                <li>
                  <NavLink
                    to={ROUTES.secteurs.entreprises}
                    className={(navData) =>
                      navData.isActive ? "active-page" : ""
                    }
                  >
                    <Icon icon='mdi:office-building-outline' className='menu-icon' />
                    Entreprises locales
                  </NavLink>
                </li>
                )}
                {canSee('page.secteurs.admin') && (
                  <li>
                    <NavLink
                      to={ROUTES.secteurs.admin}
                      end
                      className={(navData) =>
                        navData.isActive ? "active-page" : ""
                      }
                    >
                      <Icon icon='mdi:cog-outline' className='menu-icon' />
                      Administration
                    </NavLink>
                  </li>
                )}
              </ul>
            </li>
            )}
            </>)}
            </>
            )}

            {canSee('section.agenda') && (
            <>
            <RubriqueTitle
              label="Agenda"
              toneClass="sidebar-menu-group-title--agenda"
              collapsible={collapseRubriques}
              open={openRubriques.has('agenda')}
              onToggle={() => toggleRubrique('agenda')}
            />
            {showRubriqueItems('agenda') && (<>
            {canSee('page.agenda.dates') && (
            <li>
              <NavLink
                to={ROUTES.agenda.dates}
                end
                className={(navData) => (navData.isActive ? 'active-page' : '')}
              >
                <Icon icon="mdi:calendar-month-outline" className="menu-icon" />
                <span>Dates</span>
              </NavLink>
            </li>
            )}
            {canSee('page.agenda.datesAActiver') && (
            <li>
              <NavLink
                to={ROUTES.agenda.datesAActiver}
                className={(navData) => (navData.isActive ? 'active-page' : '')}
              >
                <Icon icon="mdi:calendar-check-outline" className="menu-icon" />
                <span>Dates à activer</span>
              </NavLink>
            </li>
            )}
            </>)}
            </>
            )}

            {canSee('section.iti') && (
            <>
            <RubriqueTitle
              label="itiConseil"
              toneClass="sidebar-menu-group-title--iti"
              collapsible={collapseRubriques}
              open={openRubriques.has('iti')}
              onToggle={() => toggleRubrique('iti')}
            />
            {showRubriqueItems('iti') && (<>
            {canSee('menu.iti-communication') && (
            <li className='dropdown' data-menu='iti-communication'>
              <Link to='#'>
                <Icon icon='mdi:web' className='menu-icon' />
                <span>Domaines & sites web</span>
              </Link>
              <ul className='sidebar-submenu'>
                {canSee('page.iti.hebergementSites') && (
                <li>
                  <NavLink
                    to={ROUTES.iti.hebergementSites}
                    className={(navData) =>
                      navData.isActive ? "active-page" : ""
                    }
                  >
                    <Icon icon='mdi:server-outline' className='menu-icon' />
                    Sites web
                  </NavLink>
                </li>
                )}
                {canSee('page.iti.domainesOvh') && (
                <li>
                  <NavLink
                    to={ROUTES.iti.domainesOvh}
                    className={(navData) =>
                      navData.isActive ? "active-page" : ""
                    }
                  >
                    <Icon icon='mdi:cloud-outline' className='menu-icon' />
                    Domaines OVH
                  </NavLink>
                </li>
                )}
                {canSee('page.iti.domainesGandi') && (
                <li>
                  <NavLink
                    to={ROUTES.iti.domainesGandi}
                    className={(navData) =>
                      navData.isActive ? "active-page" : ""
                    }
                  >
                    <Icon icon='mdi:shield-key-outline' className='menu-icon' />
                    Domaines Gandi
                  </NavLink>
                </li>
                )}
              </ul>
            </li>
            )}
            {canSee('page.iti.chiffrageSite') && (
            <li>
              <NavLink
                to={ROUTES.iti.chiffrageSite}
                className={(navData) =>
                  navData.isActive ? "active-page" : ""
                }
              >
                <Icon icon='mdi:file-document-edit-outline' className='menu-icon' />
                <span>Chiffrage site</span>
              </NavLink>
            </li>
            )}
            {canSee('menu.iti-maintenance') && (
            <li className='dropdown' data-menu='iti-maintenance'>
              <Link to='#'>
                <Icon icon='mdi:wrench-outline' className='menu-icon' />
                <span>Maintenance</span>
              </Link>
              <ul className='sidebar-submenu'>
                {canSee('page.iti.maintenanceMails') && (
                <li>
                  <NavLink
                    to={ROUTES.iti.maintenanceMails}
                    className={(navData) =>
                      navData.isActive ? "active-page" : ""
                    }
                  >
                    <Icon icon='mdi:email-multiple-outline' className='menu-icon' />
                    Mails sites
                  </NavLink>
                </li>
                )}
              </ul>
            </li>
            )}

            {canSee('page.iti.admin') && (
              <li>
                <NavLink
                  to={ROUTES.iti.adminSection('variables')}
                  className={isItiAdminRoute ? 'active-page' : ''}
                >
                  <Icon icon='mdi:cog-outline' className='menu-icon' />
                  <span>Administration itiConseil</span>
                </NavLink>
              </li>
            )}

            {canSee('menu.kkp-liens') && (
            <li className='dropdown' data-menu='kkp-liens'>
              <Link to='#'>
                <Icon icon='mdi:open-in-new' className='menu-icon' />
                <span>Outils</span>
              </Link>
              <ul className='sidebar-submenu'>
                {canSee('page.kkp.qualificationCommerciale') && (
                <li>
                  <a
                    href="https://bebedc-3gm9lufz.manus.space/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Icon icon='mdi:clipboard-text-outline' className='menu-icon' />
                    Qualification commerciale
                  </a>
                </li>
                )}
              </ul>
            </li>
            )}
            </>)}
            </>
            )}

{/* Menu Koikispass — Planning des ventes */}
            {canSee('section.koikispass') && (
            <>
            <RubriqueTitle
              label="Koikispass"
              toneClass="sidebar-menu-group-title--kkp"
              collapsible={collapseRubriques}
              open={openRubriques.has('kkp')}
              onToggle={() => toggleRubrique('kkp')}
            />
            {showRubriqueItems('kkp') && (<>
            {canSee('page.kkp.planDeCom') && (
            <li>
              <NavLink
                to={ROUTES.kkp.planDeCom}
                className={(navData) =>
                  navData.isActive ? "active-page" : ""
                }
              >
                <Icon icon='mdi:calendar-month-outline' className='menu-icon' />
                <span>Chiffrage Koikispass</span>
              </NavLink>
            </li>
            )}
            {canSee('menu.kkp-ventes') && (
            <li className='dropdown' data-menu='kkp-ventes'>
              <Link to='#'>
                <Icon icon='mdi:bullhorn' className='menu-icon' />
                <span>Planning des ventes</span>
              </Link>
              <ul className='sidebar-submenu'>
                {canSee('page.kkp.planningMag') && (
                <li>
                  <NavLink
                    to={ROUTES.kkp.planningMag}
                    className={(navData) =>
                      navData.isActive ? "active-page" : ""
                    }
                  >
                    <Icon icon='mdi:book-open-page-variant-outline' className='menu-icon' />
                    Planning MAG
                  </NavLink>
                </li>
                )}
                {canSee('page.kkp.planningSet') && (
                <li>
                  <NavLink
                    to={ROUTES.kkp.planningSet}
                    className={(navData) =>
                      navData.isActive ? "active-page" : ""
                    }
                  >
                    <Icon icon='mdi:table-furniture' className='menu-icon' />
                    Planning SET
                  </NavLink>
                </li>
                )}
                {canSee('page.kkp.planningNewsletter') && (
                <li>
                  <NavLink
                    to={ROUTES.kkp.planningNewsletter}
                    className={(navData) =>
                      navData.isActive ? "active-page" : ""
                    }
                  >
                    <Icon icon='mdi:email-newsletter' className='menu-icon' />
                    Planning Newsletters
                  </NavLink>
                </li>
                )}
                {canSee('page.kkp.planningWeb') && (
                <li>
                  <NavLink
                    to={ROUTES.kkp.planningWeb}
                    className={(navData) =>
                      navData.isActive ? "active-page" : ""
                    }
                  >
                    <Icon icon='mdi:web' className='menu-icon' />
                    Planning WEB
                  </NavLink>
                </li>
                )}
                {canSee('page.kkp.cdfMag') && (
                <li>
                  <NavLink
                    to={ROUTES.kkp.cdfMag}
                    end
                    className={(navData) =>
                      navData.isActive ? "active-page" : ""
                    }
                  >
                    <Icon icon='mdi:view-dashboard-outline' className='menu-icon' />
                    Chemin de fer MAG
                  </NavLink>
                </li>
                )}
                {canSee('page.kkp.cdfPubGraphiste') && (
                <li>
                  <NavLink
                    to={ROUTES.kkp.cdfPubGraphiste}
                    className={(navData) =>
                      navData.isActive ? "active-page" : ""
                    }
                  >
                    <Icon icon='mdi:palette-outline' className='menu-icon' />
                    <span className="sidebar-menu-label">Suivi créa pub</span>
                    {pubGraphisteAFaireCount > 0 && (
                      <b
                        className="sidebar-menu-notif-badge"
                        title={`${pubGraphisteAFaireCount} pub${pubGraphisteAFaireCount > 1 ? 's' : ''} à faire`}
                        aria-label={`${pubGraphisteAFaireCount} publicité${pubGraphisteAFaireCount > 1 ? 's' : ''} à faire`}
                      >
                        {pubGraphisteAFaireCount > 99 ? '99+' : pubGraphisteAFaireCount}
                      </b>
                    )}
                  </NavLink>
                </li>
                )}
                {canSee('page.kkp.bilanAudience') && (
                <li>
                  <NavLink
                    to={ROUTES.kkp.bilanAudience}
                    className={(navData) =>
                      navData.isActive ? "active-page" : ""
                    }
                  >
                    <Icon icon='mdi:chart-box-outline' className='menu-icon' />
                    Bilan audience
                  </NavLink>
                </li>
                )}
              </ul>
            </li>
            )}

            {canSee('menu.kkp-distrib') && (
            <li className='dropdown' data-menu='kkp-distrib'>
              <Link to='#'>
                <Icon icon='mdi:truck-delivery-outline' className='menu-icon' />
                <span>Distribution MAG / SET</span>
              </Link>
              <ul className='sidebar-submenu'>
                {canSee('page.kkp.distrib') && (
                <li>
                  <NavLink
                    to={ROUTES.kkp.distrib}
                    end
                    className={(navData) =>
                      navData.isActive ? "active-page" : ""
                    }
                  >
                    <Icon icon='mdi:map-marker-path' className='menu-icon' />
                    Points de dépôt
                  </NavLink>
                </li>
                )}
                {canSee('page.kkp.distrib.zones') && (
                <li>
                  <NavLink
                    to={ROUTES.kkp.distribZones}
                    className={(navData) =>
                      navData.isActive ? "active-page" : ""
                    }
                  >
                    <Icon icon='mdi:vector-polygon' className='menu-icon' />
                    Zones
                  </NavLink>
                </li>
                )}
                {canSee('page.kkp.distrib.carte') && (
                <li>
                  <NavLink
                    to={ROUTES.kkp.distribCarte}
                    className={(navData) =>
                      navData.isActive ? "active-page" : ""
                    }
                  >
                    <Icon icon='mdi:map-outline' className='menu-icon' />
                    Carte
                  </NavLink>
                </li>
                )}
                {canSee('page.kkp.distrib.carte-villes') && (
                <li>
                  <NavLink
                    to={ROUTES.kkp.distribCarteVilles}
                    className={(navData) =>
                      navData.isActive ? "active-page" : ""
                    }
                  >
                    <Icon icon='mdi:map-marker-radius' className='menu-icon' />
                    Carte des villes
                  </NavLink>
                </li>
                )}
                {canSee('page.kkp.distrib.commentaires') && (
                <li>
                  <NavLink
                    to={ROUTES.kkp.distribCommentaires}
                    className={(navData) =>
                      navData.isActive ? "active-page" : ""
                    }
                  >
                    <Icon icon='mdi:comment-text-outline' className='menu-icon' />
                    Commentaires
                  </NavLink>
                </li>
                )}
                {canSee('page.kkp.distrib.livraisons') && (
                <li>
                  <NavLink
                    to={ROUTES.kkp.distribLivraisons}
                    className={(navData) =>
                      navData.isActive ? "active-page" : ""
                    }
                  >
                    <Icon icon='mdi:calendar-clock' className='menu-icon' />
                    Dates de livraison
                  </NavLink>
                </li>
                )}
              </ul>
            </li>
            )}

            {canSee('page.kkp.sommaireMag') && (
            <li>
              <NavLink
                to={ROUTES.kkp.sommaireMag}
                className={(navData) =>
                  navData.isActive ? "active-page" : ""
                }
              >
                <Icon icon='mdi:book-open-page-variant-outline' className='menu-icon' />
                <span>Sommaire MAG</span>
              </NavLink>
            </li>
            )}

            {canSee('page.kkp.crons') && (
            <li>
              <NavLink
                to={ROUTES.kkp.crons}
                className={(navData) =>
                  navData.isActive ? "active-page" : ""
                }
              >
                <Icon icon='mdi:clock-outline' className='menu-icon' />
                <span>Alertes mails</span>
              </NavLink>
            </li>
            )}

            {canSee('page.klub.magazine') && (
            <li>
              <NavLink
                to={ROUTES.klub.magazine}
                className={(navData) =>
                  navData.isActive ? "active-page" : ""
                }
              >
                <Icon icon='mdi:newspaper-variant-outline' className='menu-icon' />
                <span>Export magazine</span>
              </NavLink>
            </li>
            )}

            {canSee('page.kkp.admin') && (
              <li>
                <NavLink
                  to={ROUTES.kkp.adminSection('mag')}
                  className={isKkpAdminRoute ? "active-page" : ""}
                >
                  <Icon icon='mdi:cog-outline' className='menu-icon' />
                  <span>Administration Koikispass</span>
                </NavLink>
              </li>
            )}

            {canSee('menu.kkp-outils') && (
            <li className='dropdown' data-menu='kkp-outils'>
              <Link to='#'>
                <Icon icon='mdi:open-in-new' className='menu-icon' />
                <span>Outils</span>
              </Link>
              <ul className='sidebar-submenu'>
                {canSee('page.kkp.calculetteRoi') && (
                <li>
                  <a
                    href="https://roicalc-msqwxcfv.manus.space/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Icon icon='mdi:calculator-variant-outline' className='menu-icon' />
                    Calculette ROI
                  </a>
                </li>
                )}
              </ul>
            </li>
            )}
            </>)}
            </>
            )}

            {/* Menu Klub */}
            {canSee('section.klub') && (
            <>
            <RubriqueTitle
              label="Klub"
              toneClass="sidebar-menu-group-title--klub"
              collapsible={collapseRubriques}
              open={openRubriques.has('klub')}
              onToggle={() => toggleRubrique('klub')}
            />
            {showRubriqueItems('klub') && (<>
            {canSee('page.klub.chiffrage') && (
            <li>
              <NavLink
                to={ROUTES.klub.chiffrage}
                className={(navData) =>
                  navData.isActive ? "active-page" : ""
                }
              >
                <Icon icon='mdi:calculator-variant-outline' className='menu-icon' />
                <span>Chiffrage klub</span>
              </NavLink>
            </li>
            )}
            {(canSee('menu.klub') || canSee('page.klub.dashboard')) && (
            <li>
              <NavLink
                to={ROUTES.klub.dashboard}
                end
                className={(navData) =>
                  navData.isActive ? "active-page" : ""
                }
              >
                <Icon icon='mdi:view-dashboard-outline' className='menu-icon' />
                <span>Dashboard Klub</span>
              </NavLink>
            </li>
            )}
            {canSee('page.klub.ce') && (
            <li>
              <NavLink
                to={ROUTES.klub.ce}
                className={(navData) =>
                  navData.isActive ? "active-page" : ""
                }
              >
                <Icon icon='mdi:briefcase-account-outline' className='menu-icon' />
                <span>Dashboard CE</span>
              </NavLink>
            </li>
            )}
            {canSee('page.klub.admin') && (
            <li>
              <NavLink
                to={ROUTES.klub.adminPipeline}
                className={isKlubAdminRoute ? "active-page" : ""}
              >
                <Icon icon='mdi:cog-outline' className='menu-icon' />
                <span>Administration Klub</span>
              </NavLink>
            </li>
            )}
            </>)}
            </>
            )}

            {canSee('section.temps') && (
            <>
            <RubriqueTitle
              label="Temps"
              toneClass="sidebar-menu-group-title--temps"
              collapsible={collapseRubriques}
              open={openRubriques.has('temps')}
              onToggle={() => toggleRubrique('temps')}
            />
            {showRubriqueItems('temps') && (<>
            {canSee('menu.temps') && (
            <li className='dropdown' data-menu='temps'>
              <Link to='#'>
                <Icon icon='mdi:clipboard-list' className='menu-icon' />
                <span>Gestion de projets</span>
              </Link>
              <ul className='sidebar-submenu'>
                {canSee('page.temps.taches') && (
                <li>
                  <NavLink
                    to={ROUTES.temps.taches}
                    className={(navData) =>
                      navData.isActive ? "active-page" : ""
                    }
                  >
                    <Icon icon='mdi:checkbox-marked-circle-outline' className='menu-icon' />
                    Mes tâches
                  </NavLink>
                </li>
                )}
                {canSee('page.temps.pilotage') && (
                <li>
                  <NavLink
                    to={ROUTES.temps.pilotage}
                    className={(navData) =>
                      navData.isActive ? "active-page" : ""
                    }
                  >
                    <Icon icon='mdi:clipboard-check-outline' className='menu-icon' />
                    Pilotage
                  </NavLink>
                </li>
                )}
              </ul>
            </li>
            )}
            </>)}
            </>
            )}

            {disabledModules.length > 0 && (
              <>
                <li className='sidebar-menu-group-title'>Autres modules</li>
                {disabledModules.map((module) => (
                  <li key={module.label} className="sidebar-menu-disabled">
                    <span className="sidebar-menu-disabled__link">
                      <Icon icon={module.icon} className='menu-icon' />
                      <span>{module.label}</span>
                      <Icon icon="solar:lock-keyhole-linear" className="sidebar-menu-disabled__lock" />
                    </span>
                  </li>
                ))}
              </>
            )}

            { (canSee('section.administration') || canSee('page.admin.clients')) && (
              <>
                <RubriqueTitle
                  label="Administration"
                  toneClass="sidebar-menu-group-title--admin"
                  collapsible={collapseRubriques}
                  open={openRubriques.has('admin')}
                  onToggle={() => toggleRubrique('admin')}
                />
                {showRubriqueItems('admin') && (<>
                {canSee('page.admin.users') && (
                <li>
                  <NavLink
                    to={ROUTES.admin.users}
                    className={(navData) =>
                      navData.isActive ? "active-page" : ""
                    }
                  >
                    <Icon icon='mdi:account-cog' className='menu-icon' />
                    <span>Utilisateurs</span>
                  </NavLink>
                </li>
                )}
                {canSee('page.admin.commerciaux') && (
                <li>
                  <NavLink
                    to={ROUTES.admin.commerciaux}
                    className={(navData) =>
                      navData.isActive ? "active-page" : ""
                    }
                  >
                    <Icon icon='mdi:account-tie-outline' className='menu-icon' />
                    <span>Commerciaux</span>
                  </NavLink>
                </li>
                )}
                {canSee('page.admin.clients') && (
                <li>
                  <NavLink
                    to={ROUTES.admin.clients}
                    className={(navData) =>
                      navData.isActive ? "active-page" : ""
                    }
                  >
                    <Icon icon='mdi:account-group-outline' className='menu-icon' />
                    <span>Base clients/Prospects</span>
                  </NavLink>
                </li>
                )}
                {canSee('page.admin.clientMapping') && (
                <li>
                  <NavLink
                    to={ROUTES.admin.clientMapping}
                    className={(navData) =>
                      navData.isActive ? "active-page" : ""
                    }
                  >
                    <Icon icon='mdi:link-variant' className='menu-icon' />
                    <span>Correspondances clients</span>
                  </NavLink>
                </li>
                )}
                {canSee('page.admin.skills') && (
                <li>
                  <NavLink
                    to={ROUTES.admin.skills}
                    className={(navData) =>
                      navData.isActive ? "active-page" : ""
                    }
                  >
                    <Icon icon='mdi:lightning-bolt-outline' className='menu-icon' />
                    <span>Skills</span>
                  </NavLink>
                </li>
                )}
                {canSee('page.admin.mcpDomains') && (
                <li>
                  <NavLink
                    to={ROUTES.admin.mcpDomains}
                    className={(navData) =>
                      navData.isActive ? "active-page" : ""
                    }
                  >
                    <Icon icon='mdi:robot-outline' className='menu-icon' />
                    <span>Domaines MCP</span>
                  </NavLink>
                </li>
                )}
                {canSee('page.admin.menuVisibility') && (
                <li>
                  <NavLink
                    to={ROUTES.admin.menuVisibility}
                    className={(navData) =>
                      navData.isActive ? "active-page" : ""
                    }
                  >
                    <Icon icon='mdi:shield-key-outline' className='menu-icon' />
                    <span>Droits des pages</span>
                  </NavLink>
                </li>
                )}
                </>)}
              </>
            )}
          </ul>
        </div>
      </aside>

      {/* Backdrop mobile : ferme le sidebar quand on clique en dehors */}
      <div
        className={`sidebar-mobile-backdrop${mobileMenu ? ' active' : ''}`}
        onClick={mobileMenuControl}
      />

      <main
        className={`${sidebarActive ? "dashboard-main active" : "dashboard-main"}${isAssistantRoute ? " dashboard-main--assistant" : ""}`}
      >
        <div className='navbar-header'>
          <div className='row align-items-center justify-content-between'>
            <div className='col-auto'>
              <div className='d-flex flex-wrap align-items-center gap-4'>
                <button
                  type='button'
                  className='sidebar-toggle'
                  onClick={sidebarControl}
                >
                  {sidebarActive ? (
                    <Icon
                      icon='iconoir:arrow-right'
                      className='icon text-2xl non-active'
                    />
                  ) : (
                    <Icon
                      icon='heroicons:bars-3-solid'
                      className='icon text-2xl non-active '
                    />
                  )}
                </button>
                <button
                  onClick={mobileMenuControl}
                  type='button'
                  className='sidebar-mobile-toggle'
                >
                  <Icon icon='heroicons:bars-3-solid' className='icon' />
                </button>
              </div>
            </div>
            <div className='col-auto'>
              <div className='d-flex flex-wrap align-items-center gap-3'>
                {user && (
                  <div className={`app-user-menu dropdown${userMenuOpen ? ' show' : ''}`} ref={userMenuRef}>
                    <button
                      className="app-user-menu__trigger"
                      type="button"
                      aria-expanded={userMenuOpen}
                      aria-haspopup="menu"
                      onClick={() => setUserMenuOpen((open) => !open)}
                    >
                      <span className="app-user-menu__avatar" aria-hidden>
                        {userInitials(user)}
                      </span>
                      <span className="app-user-menu__name d-none d-sm-inline">
                        {userShortName(user)}
                      </span>
                      <Icon icon="mdi:chevron-down" className="app-user-menu__chevron" width={16} aria-hidden />
                    </button>
                    <ul
                      className={`dropdown-menu dropdown-menu-end app-user-menu__dropdown${userMenuOpen ? ' show' : ''}`}
                      role="menu"
                    >
                      <li role="none">
                        <button
                          className="app-user-menu__item"
                          type="button"
                          role="menuitem"
                          onClick={handleLogout}
                        >
                          <Icon icon="mdi:logout" width={16} aria-hidden />
                          Déconnexion
                        </button>
                      </li>
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className='dashboard-main-body'>{children}</div>

        <footer className='d-footer'>
          <div className='row align-items-center justify-content-end'>
            <div className='col-auto text-end w-full'>
              <p className='mb-0 text-xs text-secondary-light'>© {new Date().getFullYear()} Konvergence. Tous droits réservés.</p>
            </div>
          </div>
        </footer>
      </main>
      <AssistantChatWidget />
    </section>
  );
};

export default MasterLayout;
